import { useEffect, useRef, useState } from "react";

const MAX_SECONDS = 180;
const MIMES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", ""];

export function pickMime() {
  if (typeof MediaRecorder === "undefined") return null;
  for (const m of MIMES) {
    if (!m || MediaRecorder.isTypeSupported(m)) return m;
  }
  return null;
}

export default function useVoiceRecorder() {
  const [state, setState] = useState("idle"); // idle|requesting|recording|paused|stopped|error
  const [seconds, setSeconds] = useState(0);
  const [levels, setLevels] = useState([]);
  const [error, setError] = useState(null);
  const [blob, setBlob] = useState(null);
  const [canPause, setCanPause] = useState(false);
  const [maxReached, setMaxReached] = useState(false);
  const ref = useRef({});

  const supported = typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia && typeof MediaRecorder !== "undefined";

  const cleanup = () => {
    const r = ref.current;
    clearInterval(r.timer); clearInterval(r.meter);
    try { r.recorder?.state !== "inactive" && r.recorder?.stop(); } catch { /* noop */ }
    r.stream?.getTracks().forEach((t) => t.stop());
    r.audioCtx?.close().catch(() => {});
    ref.current = {};
  };

  useEffect(() => cleanup, []);

  const start = async () => {
    if (!supported) { setError("unsupported"); setState("error"); return; }
    setError(null); setBlob(null); setSeconds(0); setLevels([]); setMaxReached(false);
    setState("requesting");
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      setError(e?.name === "NotFoundError" ? "nodevice" : "denied");
      setState("error");
      return;
    }
    const mime = pickMime();
    let recorder;
    try {
      recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    } catch {
      stream.getTracks().forEach((t) => t.stop());
      setError("unsupported"); setState("error"); return;
    }
    const chunks = [];
    recorder.ondataavailable = (e) => { if (e.data?.size) chunks.push(e.data); };
    recorder.onstop = () => {
      const b = new Blob(chunks, { type: recorder.mimeType || mime || "audio/webm" });
      setBlob(b.size > 0 ? b : null);
      if (b.size === 0) { setError("empty"); setState("error"); } else { setState("stopped"); }
      ref.current.stream?.getTracks().forEach((t) => t.stop());
      ref.current.audioCtx?.close().catch(() => {});
    };
    let audioCtx, analyser, buf;
    try {
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const src = audioCtx.createMediaStreamSource(stream);
      analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      src.connect(analyser);
      buf = new Uint8Array(analyser.frequencyBinCount);
    } catch { /* waveform optional */ }
    ref.current = { recorder, stream, audioCtx };
    recorder.start(250);
    setCanPause(typeof recorder.pause === "function");
    setState("recording");
    ref.current.timer = setInterval(() => {
      setSeconds((s) => {
        if (s + 1 >= MAX_SECONDS) { setMaxReached(true); stop(); }
        return s + 1;
      });
    }, 1000);
    if (analyser) {
      ref.current.meter = setInterval(() => {
        if (recorder.state === "paused") return;
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
        const rms = Math.sqrt(sum / buf.length);
        setLevels((l) => [...l.slice(-47), Math.min(1, rms * 4)]);
      }, 120);
    }
  };

  const pause = () => {
    const r = ref.current.recorder;
    if (r?.state === "recording" && typeof r.pause === "function") {
      r.pause(); clearInterval(ref.current.timer); setState("paused");
    }
  };
  const resume = () => {
    const r = ref.current.recorder;
    if (r?.state === "paused") {
      r.resume();
      ref.current.timer = setInterval(() => setSeconds((s) => (s + 1 >= MAX_SECONDS ? (setMaxReached(true), stop(), s) : s + 1)), 1000);
      setState("recording");
    }
  };
  const stop = () => {
    clearInterval(ref.current.timer); clearInterval(ref.current.meter);
    try { ref.current.recorder?.state !== "inactive" && ref.current.recorder?.stop(); } catch { /* noop */ }
  };
  const cancel = () => { cleanup(); setBlob(null); setSeconds(0); setLevels([]); setMaxReached(false); setState("idle"); };

  return { supported, state, seconds, levels, error, blob, canPause, maxReached, start, pause, resume, stop, cancel };
}
